/**
 * TanStack Query client + cache config + localStorage persistence.
 *
 * The QueryClient instance is module-scoped — it lives in this file's
 * top-level scope, not inside any component, so the cache survives
 * route changes without re-creation. The QueryClientProvider in
 * AppProviders.tsx just makes a reference to this instance available
 * to descendants via React context. It is plumbing, not the storage
 * layer.
 *
 * Cache lifetime decisions:
 *
 * - staleTime 5 min: report-card-writing sessions are long; the
 *   teacher's identity, subscription status, and curriculum config
 *   don't change mid-session. Treating data as fresh for 5 minutes
 *   eliminates the "fetched on every nav" feel.
 *
 * - gcTime 24h: keep unused data in cache for a day after the last
 *   consumer unmounts. Combined with the persister below, this means
 *   yesterday's data is still on disk when the teacher comes back
 *   tomorrow. The persister won't restore queries older than gcTime.
 *
 * - refetchOnWindowFocus false: the user explicitly complained
 *   about page jitter when returning to the tab. Auto-refetch on
 *   focus would re-introduce that.
 *
 * - retry 1 (queries): one retry handles transient network blips.
 *   More retries delay error surfacing.
 *
 * - retry false (mutations): never auto-retry mutations. A POST that
 *   appears to fail but actually succeeded plus a retry equals double
 *   work, double charge, double DB row.
 *
 * Persistence (added 2026-04-26):
 * The cache is persisted to localStorage so on a fresh page load the
 * teacher sees their last-session's data INSTANTLY (no
 * "Checking plan…" / "0 comments this term" loading-state flicker).
 * TanStack still refetches in the background to refresh stale data;
 * the UI doesn't shift while that happens because the placeholder
 * was already showing real (if slightly stale) numbers.
 *
 * AppProviders mounts a `<PersistQueryClientProvider>` that hydrates
 * from this persister synchronously before the app renders.
 */
import { QueryClient } from "@tanstack/react-query";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 60 * 24, // 24 hours — must exceed maxAge below
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * localStorage-backed persister. TanStack writes the entire cache
 * (queries + their data) to a single key on every mutation/refetch
 * and hydrates from it on page load.
 *
 * `key` is namespaced per environment so dev / prod don't collide
 * if a teacher uses both on the same machine.
 */
export const queryPersister = createSyncStoragePersister({
  storage: typeof window !== "undefined" ? window.localStorage : undefined,
  key: "td-query-cache",
});

/**
 * Settings consumed by `<PersistQueryClientProvider>` in AppProviders.
 *
 * - maxAge 24h: anything older than this is discarded on hydrate.
 *   Same as gcTime so the two stay aligned.
 * - buster: bump this string when the cache shape changes (e.g. when
 *   we change a query's response shape). Old caches with a different
 *   buster get discarded on next page load.
 */
export const queryPersistOptions = {
  persister: queryPersister,
  maxAge: 1000 * 60 * 60 * 24,
  buster: "v1",
};
